import './Footer.css';

export interface FooterProps {
  onGoDocs: () => void;
  onGoPricing: () => void;
  /**
   * The DPA & sub-processor register page (added 2026-08-14 — the doc was
   * counsel-cleared since 2026-08-08 and marked "Yes — publish" but nothing
   * ever rendered it). Optional so a page that already IS the legal page
   * (Legal.tsx) can omit it rather than link to itself.
   */
  onGoLegal?: () => void;
}

/**
 * ⚠️ **Every link carries `land-footer__link` and nothing else — 2026-08-18.**
 *
 * Three of these were `land-btn land-btn--plain land-footer__link` and three were bare `<a>` with no
 * class. `.land-btn` sets `font-weight: 600` and `display: inline-flex`, so half the row rendered
 * **semibold on a different baseline** to the other half, and `.land-btn--plain`'s `font-size: 14px`
 * fought `.land-footer__link`'s `12.5px` at equal specificity — which won depended on CSS import
 * order. Reported by the owner as *"some of them has different formating and it looks uneven"*, which
 * is exactly what it was.
 *
 * A footer link is **navigation text, not a button**, so the button classes are removed rather than
 * overridden. The `<button>` elements stay where a callback is genuinely needed; `Footer.css`
 * normalises the two element types to render identically.
 */
export function Footer({ onGoDocs, onGoPricing, onGoLegal }: FooterProps) {
  return (
    <div className="land-footer land-container">
      <span className="land-footer__brand">
        <span className="land-footer__wordmark">dedupr</span>
        <span className="land-footer__dot" />
        <span className="land-footer__copy">© 2026 Dedupr Ltd · London</span>
      </span>
      <div className="land-footer__links">
        <button type="button" className="land-footer__link" onClick={onGoDocs}>
          Documentation
        </button>
        <button type="button" className="land-footer__link" onClick={onGoPricing}>
          Pricing
        </button>
        <a className="land-footer__link" href="#systems">
          Connectors
        </a>
        <a className="land-footer__link" href="#decide">
          Security
        </a>
        {/* Residency detail itself stays on the pricing page's enterprise
            section (#pricing); the DPA moved to its own page 2026-08-14 —
            see docs/landing/dpa-and-subprocessors.md, counsel-cleared
            2026-08-08 and never wired to a route until now. */}
        {onGoLegal && (
          <button type="button" className="land-footer__link" onClick={onGoLegal}>
            DPA &amp; sub-processors
          </button>
        )}
        {/* ⚠️ **Unconditional, unlike the DPA link above.** `page-structure.md` has specified
            *"Footer: privacy policy, DPA on request, security posture, contact"* since it was
            written, and these two were the items never built. A footer that renders a privacy or
            terms link only when a prop happens to be passed is a link that will be missing on some
            page — and Google's OAuth verification checks the policy is reachable from the site, not
            merely that a URL exists. */}
        <a className="land-footer__link" href="#/privacy">
          Privacy
        </a>
        <a className="land-footer__link" href="#/terms">
          Terms
        </a>
      </div>
    </div>
  );
}
